import { Nav, Navbar } from 'react-bootstrap';
import { Route, routes } from 'sitemap';
import { capitalize } from 'helpers/utils';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import { useAppContext } from 'providers/AppProvider';
import Button from 'components/base/Button';
import NavbarTopNav from '../navbar-horizontal/NavbarTopNav';
import { useBreakpoints } from 'providers/BreakpointsProvider';
import NavbarVerticalCollapseProvider from './NavbarVerticalCollapseProvider';
import { Branch } from 'providers/BranchProvider';
import { faArrowRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect } from 'react';
import { AuthContext } from 'providers/AuthProvider';
import { hasAnyRole } from 'utils/roleAccess';
import { useAwaitingProcessingCounts } from 'hooks/useAwaitingProcessingCounts';

const filterRoutesByRole = (
  pages: Route[],
  userRoles: Array<{ item2?: string }> | undefined
): Route[] => {
  return pages
    .map(page => {
      const filteredChildren = page.pages
        ? filterRoutesByRole(page.pages, userRoles)
        : undefined;

      if (page.requiredRole && !hasAnyRole(userRoles, page.requiredRole)) {
        return null;
      }

      if (page.pages) {
        if (!filteredChildren?.length) {
          return null;
        }
        return { ...page, pages: filteredChildren };
      }

      return page;
    })
    .filter((page): page is Route => page !== null);
};

const NavbarVertical = ({ branchDomain }: { branchDomain: Branch | null }) => {
  const {
    config: {
      navbarPosition,
      openNavbarVertical,
      navbarVerticalAppearance,
      isNavbarVerticalCollapsed
    }
  } = useAppContext();

  const { breakpoints } = useBreakpoints();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('AuthContext must be used within an AuthContextProvider');
  }

  const { token, dispatch, isImpersonating, setIsImpersonating } = authContext;
  const { data: awaitingCounts } = useAwaitingProcessingCounts(
    branchDomain?.branchDomain
  );

  const handleEndImpersonation = async (e: React.FormEvent) => {
    e.preventDefault();
    const originalToken = localStorage.getItem('impersonateToken');
    if (isImpersonating && originalToken) {
      await dispatch({
        type: 'END_IMPERSONATE',
        payload: JSON.parse(originalToken as string)
      });
      setIsImpersonating(false);
    }
  };

  useEffect(() => {
    const checkImpersonation = () => {
      setIsImpersonating(!!localStorage.getItem('impersonateToken'));
    };

    checkImpersonation();
    window.addEventListener('storage', checkImpersonation);
    return () => window.removeEventListener('storage', checkImpersonation);
  }, []);

  useEffect(() => {
    setIsImpersonating(!!localStorage.getItem('impersonateToken'));
  }, [token]);

  return (
    <NavbarVerticalCollapseProvider>
      <Navbar
        className="navbar-vertical"
        expand="lg"
        variant=""
        data-navbar-appearance={
          navbarVerticalAppearance === 'darker' ? 'darker' : ''
        }
      >
        <Navbar.Collapse id="navbarVerticalCollapse" in={openNavbarVertical}>
          <div
            className="navbar-vertical-content"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <Nav className="flex-column" as="ul" id="navbarVerticalNav">
              {routes.map(route => (
                <Nav.Item key={route.label}>
                  {!route.labelDisabled && (
                    <>
                      <p className="navbar-vertical-label">
                        {capitalize(route.label)}
                      </p>
                      <hr className="navbar-vertical-line" />
                    </>
                  )}
                  <NavbarVerticalMenu
                    level={1}
                    awaitingCounts={awaitingCounts}
                    routes={filterRoutesByRole(route.pages, token?.roles).map(
                      page => ({
                        ...page,
                        path: branchDomain
                          ? page?.path?.replace(
                              ':branchDomain',
                              branchDomain.branchDomain
                            )
                          : page?.path
                      })
                    )}
                  />
                </Nav.Item>
              ))}
            </Nav>

            {navbarPosition === 'combo' && breakpoints.down('lg') && (
              <div className="move-container">
                <div className="navbar-vertical-divider">
                  <hr className="navbar-vertical-hr" />
                </div>
                <NavbarTopNav />
              </div>
            )}

            {isImpersonating && (
              <Button
                variant="phoenix-light"
                className="text-start"
                startIcon={
                  <FontAwesomeIcon
                    icon={faArrowRightFromBracket}
                    style={{ marginRight: '1rem' }}
                  />
                }
                onClick={handleEndImpersonation}
              >
                {!isNavbarVerticalCollapsed && 'End Impersonation'}
              </Button>
            )}
          </div>
        </Navbar.Collapse>
      </Navbar>
    </NavbarVerticalCollapseProvider>
  );
};

export default NavbarVertical;
